import { Injectable, Injector } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State } from '@ordersnproducts/modules/one-click-order/reducers';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { distinctUntilChangedDeep } from '@ordersnproducts/modules/shared/utils/distinct-until-changed-deep';
import { isNullOrUndefined } from '@util';
import { GlobalAnalyticsDraft } from '../modules/global-analytics-draft';
import { AnalyticsTrackingBase } from './analytics-tracking-base';
import { TranslateService } from '@ngx-translate/core';
import { ANALYTICS_DEFAULT_LANGUAGE } from 'app/modules/google-analytics-type';
import {
  GoogleAnalyticsEventAction,
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEventLabel,
  GoogleAnalyticsEventStatus,
  GoogleAnalyticsEventType,
  PrefillButtonEvent,
} from 'app/modules/google-analytics-event-type';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil, map, tap, switchMap, take } from 'rxjs/operators';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { OrderCreationIcon } from '@ordersnproducts/modules/order-builder/models/order-strategy-response';
import { environment } from 'environments/environment';
import { SessionService, CountlyService } from '@cemex-core/angular-services-v8';
import { isUnloadTypePumpValue } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/pump-primer-helper';

export const CEMEX_GO = 'Cemex Go';
export const APPLICATION = 'Orders-and-products';

@Injectable()
export class AnalyticsTrackingService extends AnalyticsTrackingBase {
  public state: any;
  private userType: any;
  private environment = environment.name;
  private userId: number;
  private countryCode: string;

  private translations: { [key: string]: string };

  constructor(
    public store: Store<State>,
    public injector: Injector,
    private countlyService: CountlyService,
    private translateService: TranslateService,
    private router: Router,
    private sessionService: SessionService,
  ) {
    super();

    this.translateService.getTranslation(ANALYTICS_DEFAULT_LANGUAGE).subscribe((translations) => {
      this.translations = translations;
    });
  }

  private translate(key: string): string {
    return this.translations[key];
  }

  private getPageLabel(urlToConvert: string) {
    const workingString = urlToConvert.replace(/\/app\//g, '').replace(/\//g, '_');
    const capital = workingString.slice(0, 1).toUpperCase();
    return capital + workingString.slice(1);
  }

  setPageView(customerId?: string) {
    const pageTitle = `S2-${this.getPageLabel(this.router.url.trim())}`;
    this.countlyService.addGTM(APPLICATION, pageTitle, customerId);
  }

  initAnalyticsTracking() {
    const legalEntity$ = this.store.pipe(
      select(getLegalEntity),
      filter((legalEn) => !!legalEn),
      tap((le) => {
        this.userType = _safe(() => le.legalEntityType.legalEntityTypeDesc);
        this.countryCode = _safe(() => le.countryCode);
      }),
    );
    legalEntity$.subscribe();

    const navigationUrl$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.url),
    );

    // FIXME: State its not typed, it's an arbitrary object. It's not a good idea to tap into the state directly like this.
    // There's no way to know while refactoring if this breaks. We should be firing the events using ngrx
    this.store.pipe(distinctUntilChangedDeep()).subscribe((state) => {
      this.state = state;

      try {
        const googleAnalyticsState = this.googleAnalyticsRequest;
        let googleAnalyticsRequest: any;

        if (this.stepChanged(googleAnalyticsState.activeStep)) {
          if (this.isActive(0)) {
            googleAnalyticsRequest = this.createBusinessLinePageLoadedReport();
          } else if (this.isActive(1)) {
            googleAnalyticsRequest = this.createLocationPageLoadedReport(googleAnalyticsState);
          } else if (this.isActive(2)) {
            googleAnalyticsRequest = this.createSpecificationPageLoadedReport(googleAnalyticsState);
          } else if (this.isActive(3)) {
            googleAnalyticsRequest = this.createSpecificationPageReport(googleAnalyticsState);
            this.countlyService.addBulkVariables(googleAnalyticsRequest);
            googleAnalyticsRequest = this.createReviewPageLoadedReport(googleAnalyticsState);
          } else if (this.isActive(4)) {
            googleAnalyticsRequest = this.createSummaryPageLoaded(googleAnalyticsState);
          }
          this.countlyService.addBulkVariables(googleAnalyticsRequest);
        }
      } catch (e) {
        console.log('error', e);
      }
    });

    // Track page views and location related events
    // TODO: Move to effects
    navigationUrl$
      .pipe(
        switchMap((url) =>
          legalEntity$.pipe(
            map((le) => le.legalEntityTypeCode),
            take(1),
            tap((legalEntityTypeCode) => this.setPageView(legalEntityTypeCode)),
            map(() => url),
          ),
        ),
      )
      .subscribe((url) => {
        // Events triggered after a specific Pageview event
        switch (url) {
          /* Repeat order */
          case '/app/new-repeat-order/location-specification':
            this.countlyService.addBulkVariables({
              event: GoogleAnalyticsEventType.Checkout,
              eventCategory: GoogleAnalyticsEventCategory.NewOrder,
              eventAction: GoogleAnalyticsEventAction.RepeatOrder,
              eventLabel: GoogleAnalyticsEventLabel.OrderStep_Specifications,
            });
            break;
          case '/app/new-repeat-order/review':
            this.countlyService.addBulkVariables({
              event: GoogleAnalyticsEventType.Checkout,
              eventCategory: GoogleAnalyticsEventCategory.NewOrder,
              eventAction: GoogleAnalyticsEventAction.RepeatOrder,
              eventLabel: GoogleAnalyticsEventLabel.OrderStep_Review,
              jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
              pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
              deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
              productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
            });
            break;
          case '/app/order-confirmation':
            // This property being defined indicates a Repeat Order
            if (
              _safe(() => this.state['NewRepeatOrder']['order']['loading']) &&
              _safe(() => this.state['orderConfirmation']['orderData']) &&
              this.state['orderConfirmation']['orderData']['icon'] !== OrderCreationIcon.Error
            ) {
              const analyticsDataRepeat = {
                event: GoogleAnalyticsEventType.Checkout,
                eventCategory: GoogleAnalyticsEventCategory.NewOrder,
                eventAction: GoogleAnalyticsEventAction.RepeatOrder,
                eventLabel: GoogleAnalyticsEventLabel.OrderStep_Success,
                jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
                pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
                deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
                productLine: _safe(
                  () => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc,
                ),
                orderId: _safe(
                  () =>
                    this.state.orderBuilder.rmsSummaryPage.rmsLegacyOrderState.orderResponse.MT_Orders_resp.Orders[0]
                      .SalesDocument,
                ),
              };
              this.addPumpPrimerFieldsToAnalyticsData(analyticsDataRepeat, this.state.orderBuilder);
              this.countlyService.addBulkVariables(analyticsDataRepeat);
            } else if (
              _safe(() => this.state['NewModifyOrder']['order']['loading']) &&
              _safe(() => this.state['orderConfirmation']['orderData']) &&
              this.state['orderConfirmation']['orderData']['icon'] !== OrderCreationIcon.Error
            ) {
              const analyticsDataModify = {
                event: GoogleAnalyticsEventType.Checkout,
                eventCategory: GoogleAnalyticsEventCategory.NewOrder,
                eventAction: GoogleAnalyticsEventAction.ModifyOrder,
                eventLabel: GoogleAnalyticsEventLabel.OrderStep_Success,
                jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
                pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
                deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
                productLine: _safe(
                  () => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc,
                ),
                orderId: this.currentOrderId,
              };
              this.addPumpPrimerFieldsToAnalyticsData(analyticsDataModify, this.state.orderBuilder);
              this.countlyService.addBulkVariables(analyticsDataModify);
            } else if (
              !_safe(() => this.state['NewModifyOrder']['order']['loading']) &&
              !_safe(() => this.state['NewRepeatOrder']['order']['loading']) &&
              _safe(() => this.state['orderConfirmation']['orderData']) &&
              this.state['orderConfirmation']['orderData']['icon'] !== OrderCreationIcon.Error
            ) {
              const analyticsDataCreate = {
                event: GoogleAnalyticsEventType.Checkout,
                eventCategory: GoogleAnalyticsEventCategory.NewOrder,
                eventAction: GoogleAnalyticsEventAction.OrderRequest,
                eventLabel: GoogleAnalyticsEventLabel.OrderStep_Success,
                jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
                pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
                deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
                productLine: _safe(
                  () => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc,
                ),
                orderId: _safe(
                  () =>
                    this.state.orderBuilder.rmsSummaryPage.rmsLegacyOrderState.orderResponse.MT_Orders_resp.Orders[0]
                      .SalesDocument,
                ),
              };
              this.addPumpPrimerFieldsToAnalyticsData(analyticsDataCreate, this.state.orderBuilder);
              this.countlyService.addBulkVariables(analyticsDataCreate);
            }
            break;
          /* Modify order */
          case '/app/new-modify-order/location-specification':
            this.countlyService.addBulkVariables({
              event: GoogleAnalyticsEventType.Checkout,
              eventCategory: GoogleAnalyticsEventCategory.NewOrder,
              eventAction: GoogleAnalyticsEventAction.ModifyOrder,
              eventLabel: GoogleAnalyticsEventLabel.OrderStep_Specifications,
            });
            break;
          case '/app/multiple-order-online/orders-confirmation-summary':
            this.countlyService.addBulkVariables({
              event: GoogleAnalyticsEventType.Checkout,
              eventCategory: GoogleAnalyticsEventCategory.NewOrder,
              eventAction: GoogleAnalyticsEventAction.Successful,
              eventLabel: GoogleAnalyticsEventLabel.OrderStep_Summary,
              jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
              pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
              deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
              productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
            });
            break;
          case '/app/new-modify-order/review':
            this.store
              .pipe(
                takeUntil(
                  this.store.pipe(
                    filter(
                      (x) =>
                        x['orderBuilder'].rmsSummaryPage.rmsLegacyOrderState.orderResponse != null ||
                        x['orderBuilder'].rmsSummaryPage.rmsLegacySimulateState.simulateResponse != null,
                    ),
                  ),
                ),
              )
              .subscribe({
                next: null,
                error: null,
                complete: () => {
                  this.countlyService.addBulkVariables({
                    event: GoogleAnalyticsEventType.Checkout,
                    eventCategory: GoogleAnalyticsEventCategory.NewOrder,
                    eventAction: GoogleAnalyticsEventAction.ModifyOrder,
                    eventLabel: GoogleAnalyticsEventLabel.OrderStep_Review,
                    jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
                    pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
                    deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
                    productLine: _safe(
                      () => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc,
                    ),
                    orderId: this.currentOrderId,
                  });
                },
              });
            break;
        }
      });

    const profile = this.sessionService.profile;
    this.userId = _safe(() => profile.userId);
  }

  /**
   * @description Add pump primer fields to analytics data
   *
   * @param {any} analyticsData
   * @param {any} order
   * @returns
   * @memberof AnalyticsTrackingService
   * @private
   * */
  private addPumpPrimerFieldsToAnalyticsData(analyticsData, order) {
    const unloadType = _safe(
      () => this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.unloadType,
    );
    const unloadTypePumpValues = this.state.orderBuilder.rmsSpecificationPage.PumpPrimerMixState.unloadTypePumpValues;
    const isPumpUnloadType = isUnloadTypePumpValue(unloadType, unloadTypePumpValues);
    if (isPumpUnloadType) {
      analyticsData['unloadType'] = _safe(
        () => this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.unloadType,
        '',
      );
      analyticsData['pump_primer'] = _safe(
        () => this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.pumpPrimerProduct,
        '',
      );
      analyticsData['primer_quantity'] = _safe(
        () => this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.primerQuantity,
        '',
      );
      analyticsData['primer_arrival'] = _safe(
        () =>
          this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values
            .primerArrivalBeforeDelivery,
        '',
      );
      analyticsData['primer_slump'] = _safe(
        () => this.state.orderBuilder.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.primerSlump,
        '',
      );
    }
  }

  // TODO: Add to googleAnalyticsRequest object OR/AND make a selector instead
  private get currentOrderId(): string {
    return (
      _safe(
        () =>
          this.state.orderBuilder.rmsSummaryPage.rmsLegacyOrderState.orderResponse.MT_Orders_resp.Orders[0]
            .SalesDocument,
      ) ||
      _safe(
        () =>
          this.state.orderBuilder.rmsSummaryPage.rmsLegacySimulateState.simulateResponse.MT_Orders_resp.Orders[0]
            .SalesDocument,
      )
    );
  }

  // FIXME: This should've been a selector maybe
  get googleAnalyticsRequest(): any {
    return new GlobalAnalyticsDraft(this.state['orderBuilder'], this.injector, this.translate.bind(this))
      .getDeliveryMethod()
      .getProductLineDesc()
      .getProductDescription()
      .getQuantity()
      .getUnit()
      .getPaymentTerm()
      .getOrderId()
      .getCleanUp()
      .getProductId()
      .getDeliveryDate()
      .getDeliveryTime()
      .getPointOfDeliveryId()
      .getJobsiteId()
      .getStep();
  }

  orderFinishedReports(status, orderId?: string): void {
    const googleAnalyticsState = this.googleAnalyticsRequest;
    let googleAnalyticsRequest = this.createSummaryPageReport(googleAnalyticsState, orderId);
    this.countlyService.addBulkVariables(googleAnalyticsRequest);
    googleAnalyticsRequest = this.createOrderStatusReport(googleAnalyticsState, status, orderId);
    this.countlyService.addBulkVariables(googleAnalyticsRequest);
  }

  noSlotAvailableReport(): void {
    const googleAnalyticsRequest = this.createReportNoSlotAvailable();
    this.countlyService.addBulkVariables(googleAnalyticsRequest);
  }

  onHoldAnalytics(orderId: number, holdStatus: GoogleAnalyticsEventStatus) {
    const googleAnalyticsRequest = this.createOnHoldReport(orderId, holdStatus);
    this.countlyService.addBulkVariables(googleAnalyticsRequest);
  }

  resumeAnalytics(orderId: number, resumeStatus: GoogleAnalyticsEventStatus) {
    const googleAnalyticsRequest = this.createResumeReport(orderId, resumeStatus);
    this.countlyService.addBulkVariables(googleAnalyticsRequest);
  }

  adjustmentAnalytics(orderId: string, status: GoogleAnalyticsEventStatus) {
    const action = {
      intent: GoogleAnalyticsEventAction.AdjustOrder,
      error: GoogleAnalyticsEventAction.Rejected,
      success: GoogleAnalyticsEventAction.Successful,
    };

    this.countlyService.addBulkVariables({
      event: GoogleAnalyticsEventType.Custom,
      eventCategory: GoogleAnalyticsEventCategory.Adjustments,
      eventAction: action[status],
      orderId: orderId.toString(),
    });
  }

  downloadOrderConfirmation(orderId: string, action: GoogleAnalyticsEventAction) {
    this.countlyService.addBulkVariables({
      event: GoogleAnalyticsEventType.Custom,
      eventCategory: GoogleAnalyticsEventCategory.Downloads,
      eventAction: action,
      eventLabel: orderId,
    });
  }

  cancelOrder(orderId: number, cancelStatus: GoogleAnalyticsEventStatus) {
    const report = this.createCancelOrderReport(orderId, cancelStatus);
    this.countlyService.addBulkVariables(report);
  }

  repeatOrder() {
    return {
      finalStep: () => {
        this.countlyService.addBulkVariables({
          event: GoogleAnalyticsEventType.Checkout,
          eventCategory: GoogleAnalyticsEventCategory.NewOrder,
          eventAction: GoogleAnalyticsEventAction.RepeatOrder,
          eventLabel: GoogleAnalyticsEventLabel.OrderStep_FinalStep,
          jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
          pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
          deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
          productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
        });
      },

      confirmationError: () => {
        this.countlyService.addBulkVariables({
          event: GoogleAnalyticsEventType.Checkout,
          eventCategory: GoogleAnalyticsEventCategory.NewOrder,
          eventAction: GoogleAnalyticsEventAction.RepeatOrder,
          eventLabel: GoogleAnalyticsEventLabel.OrderStep_Error,
          jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
          pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
          productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
          deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
        });
      },
    };
  }

  modifyOrder() {
    return {
      finalStep: () => {
        this.countlyService.addBulkVariables({
          event: GoogleAnalyticsEventType.Checkout,
          eventCategory: GoogleAnalyticsEventCategory.NewOrder,
          eventAction: GoogleAnalyticsEventAction.ModifyOrder,
          eventLabel: GoogleAnalyticsEventLabel.OrderStep_FinalStep,
          jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
          pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
          deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
          productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
          orderId: this.currentOrderId,
        });
      },

      confirmationError: () => {
        this.countlyService.addBulkVariables({
          event: GoogleAnalyticsEventType.Checkout,
          eventCategory: GoogleAnalyticsEventCategory.NewOrder,
          eventAction: GoogleAnalyticsEventAction.ModifyOrder,
          eventLabel: GoogleAnalyticsEventLabel.OrderStep_Error,
          jobsiteId: this.googleAnalyticsRequest.jobsiteId || '',
          pointOfDeliveryId: this.googleAnalyticsRequest.pointOfDeliveryId || '',
          deliveryMethod: this.googleAnalyticsRequest.deliveryMethod || '',
          productLine: _safe(() => this.state.orderBuilder.businessLinePage.businessLine.selected.productTypeDesc),
          orderId: this.currentOrderId,
        });
      },
    };
  }

  confirmToDispatch() {
    return {
      confirmToDispatchNewOrder: (order) => {
        this.dispatchEvent(GoogleAnalyticsEventType.ConfirmToDispatch, order, GoogleAnalyticsEventLabel.NewOrder);
      },
      confirmToDispatchMultipleOrder: (order) => {
        this.dispatchEvent(GoogleAnalyticsEventType.ConfirmToDispatch, order, GoogleAnalyticsEventLabel.MultipleOrder);
      },
      confirmToDispatchOrderDetail: (order) => {
        this.dispatchEvent(
          GoogleAnalyticsEventType.ConfirmToDispatch,
          order.payload,
          GoogleAnalyticsEventLabel.OrderDetail,
        );
      },
      readyToReceive: (order, location: GoogleAnalyticsEventLabel) => {
        this.dispatchEvent(GoogleAnalyticsEventType.ReadyToReceive, order.payload, location);
      },
    };
  }

  /**
   * @description Dispatch analytics event about Confirm to Dispatch
   * @param {GoogleAnalyticsEventType} event event name
   * @param order order object, the structure can be different based on location
   * @param {GoogleAnalyticsEventLabel} location place in application
   */
  private dispatchEvent(event: GoogleAnalyticsEventType, order, location: GoogleAnalyticsEventLabel) {
    this.countlyService.addBulkVariables({
      event: event,
      application: CEMEX_GO,
      countryCode: this.countryCode,
      customerCode: _safe(() => order.customer.customerCode) || '',
      environment: this.environment,
      jobsiteCode: _safe(() => order.jobsite.jobsiteCode) || '',
      pagePath: _safe(() => window.location.pathname) || '',
      pageTitle: _safe(() => window.location.pathname.split('/').pop()) || '',
      orderId: _safe(() => order.orderId),
      userId: this.userId,
      userType: this.userType,
      confirmDispatchLocation: location,
    });
  }

  private isActive(i: number) {
    return i === this.activeStep;
  }

  private stepChanged(step: number): boolean {
    if (!isNullOrUndefined(step) && this.activeStep !== step) {
      if (this.activeStep > 2) {
        this.changeCheckoutStep(this.activeStep < step ? true : false);
      }
      this.activeStep = step;
      return true;
    }
    return false;
  }

  deletePod(channel: string, jobsite: string, pod_name: string, user: string, disable_date_user: string) {
    this.countlyService.addBulkVariables({
      event: GoogleAnalyticsEventType.DeletePod,
      channel: channel,
      jobsite: jobsite,
      pod_name: pod_name,
      user: user,
      disable_date_user: disable_date_user,
    });
  }

  PODCreationFails(channel: string, jobsiteName: string, address: string, pod_creation_fail: string) {
    this.countlyService.addBulkVariables({
      event: GoogleAnalyticsEventType.PODCreationFail,
      channel: channel,
      user: jobsiteName,
      address: address,
      pod_creation_fail: pod_creation_fail,
    });
  }

  PODCreationSuccess(channel: string, jobsiteName: string, address: string, pod_creation: string) {
    this.countlyService.addBulkVariables({
      event: GoogleAnalyticsEventType.PODCreationSuccess,
      channel: channel,
      user: jobsiteName,
      address: address,
      pod_creation: pod_creation,
    });
  }

  public prefillButtonEvent() {
    const { userSegment, userSubsegment, currentLegalEntityValue, profile, role, country } = this.sessionService;
    const { isDigitalAdmin, userId } = profile || {};

    const prefillEvent = {
      event: GoogleAnalyticsEventType.PrefillButton,
      userId: userId?.toString() || '',
      countryCode: country || '',
      customerId: currentLegalEntityValue?.legalEntityTypeCode || '',
      userSegment: userSegment || '',
      userSubsegment: userSubsegment || '',
      userCategory: isDigitalAdmin ? 'DA' : 'General',
      userType: role === 'C' ? 'Customer' : 'Internal',
      pagePath: window.location.pathname,
      pageTitle: `S2-${this.getPageLabel(this.router.url.trim())}`,
      application: APPLICATION,
      jobsiteCode: this.googleAnalyticsRequest.jobsiteId || '',
    } satisfies PrefillButtonEvent;

    this.countlyService.addBulkVariables(prefillEvent);
  }
}
